import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Wordmark } from '@/components/ui/Logo';
import { useBodyScrollLock } from '@/hooks';
import { useAuth } from '@/hooks/auth';
import { PROTECTED_ROUTES, PUBLIC_ROUTES } from '@/utils';

const NAV_LINKS = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: PUBLIC_ROUTES.PRICING },
  { label: 'About', href: PUBLIC_ROUTES.ABOUT },
  { label: 'FAQ', href: PUBLIC_ROUTES.FAQ },
  { label: 'Contact', href: PUBLIC_ROUTES.CONTACT },
] as const;

const SCROLL_THRESHOLD = 64;

export function PublicNavbar(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pendingScrollId = useRef<string | null>(null);

  const isLandingPage = location.pathname === '/';

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, location.hash, closeMobileMenu]);

  // Hold the page's scroll while the menu is open — counted with every other
  // overlay's.
  useBodyScrollLock(isMobileMenuOpen);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActiveLink = (href: string): boolean => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && location.hash === href.slice(1);
    }
    return location.pathname === href;
  };

  useEffect(() => {
    if (location.pathname !== '/' || !pendingScrollId.current) return;
    const id = pendingScrollId.current;
    pendingScrollId.current = null;

    let attempts = 0;
    const MAX_ATTEMPTS = 20;
    const interval = setInterval(() => {
      const target = document.getElementById(id);
      if (target) {
        clearInterval(interval);
        target.scrollIntoView({ behavior: 'smooth' });
      } else if (++attempts >= MAX_ATTEMPTS) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleHashLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      const id = href.replace('/#', '');

      if (location.pathname === '/') {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        pendingScrollId.current = id;
        navigate({ pathname: '/', hash: `#${id}` });
      }
    },
    [location.pathname, navigate]
  );

  const showSolidBackground = !isLandingPage || isScrolled;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-[background-color,border-color,box-shadow] duration-normal ease-in-out motion-reduce:transition-none ${
        showSolidBackground
          ? 'bg-contrast-950 border-b border-stroke-default'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <nav
        aria-label="Main navigation"
        className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 lg:h-16 lg:px-8"
      >
        {/* Logo — the Dō wordmark */}
        <Link
          to={PUBLIC_ROUTES.HOME}
          className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-state-focus"
        >
          <Wordmark size={28} />
        </Link>

        {/* Desktop Navigation Links */}
        <div className="hidden lg:flex lg:items-center lg:gap-8">
          {NAV_LINKS.map((link) => {
            const isActive = isActiveLink(link.href);
            const isHashLink = link.href.startsWith('/#');

            return isHashLink ? (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleHashLinkClick(e, link.href)}
                aria-current={isActive ? 'page' : undefined}
                className={`text-body-md transition-colors rounded-md px-2 py-1 -mx-2 focus-visible:outline-none focus-visible:ring-state-focus ${
                  isActive
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-400 hover:text-neutral-50'
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                aria-current={isActive ? 'page' : undefined}
                className={`text-body-md transition-colors rounded-md px-2 py-1 -mx-2 focus-visible:outline-none focus-visible:ring-state-focus ${
                  isActive
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-400 hover:text-neutral-50'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Desktop CTA Section */}
        <div className="hidden lg:flex lg:items-center lg:gap-4">
          {isAuthenticated ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(PROTECTED_ROUTES.DASHBOARD)}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Link
                to={PUBLIC_ROUTES.LOGIN}
                aria-current={
                  location.pathname === PUBLIC_ROUTES.LOGIN ? 'page' : undefined
                }
                className={`text-body-md transition-colors rounded-md px-2 py-1 focus-visible:outline-none focus-visible:ring-state-focus ${
                  location.pathname === PUBLIC_ROUTES.LOGIN
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-400 hover:text-neutral-50'
                }`}
              >
                Login
              </Link>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(PUBLIC_ROUTES.REGISTER)}
              >
                Get Started
              </Button>
            </>
          )}
        </div>

        {/* Tablet/Mobile CTA Section */}
        <div className="flex items-center gap-3 lg:hidden">
          {isAuthenticated ? (
            <Button
              variant="primary"
              size="sm"
              className="hidden md:inline-flex"
              onClick={() => navigate(PROTECTED_ROUTES.DASHBOARD)}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Link
                to={PUBLIC_ROUTES.LOGIN}
                aria-current={
                  location.pathname === PUBLIC_ROUTES.LOGIN ? 'page' : undefined
                }
                className={`hidden text-body-md transition-colors rounded-md px-2 py-1 focus-visible:outline-none focus-visible:ring-state-focus md:block ${
                  location.pathname === PUBLIC_ROUTES.LOGIN
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-400 hover:text-neutral-50'
                }`}
              >
                Login
              </Link>
              <Button
                variant="primary"
                size="sm"
                className="hidden md:inline-flex"
                onClick={() => navigate(PUBLIC_ROUTES.REGISTER)}
              >
                Get Started
              </Button>
            </>
          )}

          {/* Hamburger menu button */}
          <button
            type="button"
            onClick={toggleMobileMenu}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-400 hover:bg-contrast-600 transition-colors focus-visible:outline-none focus-visible:ring-state-focus"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-scrim-strong lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel — dark surface */}
      <div
        id="mobile-menu"
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-contrast-950 shadow-lift-sheet transition-transform duration-slow ease-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        {/* Mobile menu header */}
        <div className="flex h-14 items-center justify-between border-b border-stroke-default px-4">
          <Link
            to={PUBLIC_ROUTES.HOME}
            className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-state-focus"
            onClick={closeMobileMenu}
          >
            <Wordmark size={28} />
          </Link>
          <button
            type="button"
            onClick={closeMobileMenu}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-400 hover:bg-contrast-600 transition-colors focus-visible:outline-none focus-visible:ring-state-focus"
            aria-label="Close menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile nav links */}
        <nav aria-label="Mobile navigation" className="flex flex-col px-4 py-4">
          {NAV_LINKS.map((link) => {
            const isActive = isActiveLink(link.href);
            const isHashLink = link.href.startsWith('/#');

            return isHashLink ? (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  handleHashLinkClick(e, link.href);
                  closeMobileMenu();
                }}
                aria-current={isActive ? 'page' : undefined}
                className={`border-b border-stroke-default py-3 text-heading-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-state-focus-inset ${
                  isActive
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-300 hover:text-neutral-50'
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                onClick={closeMobileMenu}
                aria-current={isActive ? 'page' : undefined}
                className={`border-b border-stroke-default py-3 text-heading-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-state-focus-inset ${
                  isActive
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-300 hover:text-neutral-50'
                }`}
              >
                {link.label}
              </Link>
            );
          })}

          {/* Divider */}
          <div className="my-4 border-t border-stroke-default" />

          {isAuthenticated ? (
            /* Go to Dashboard button */
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => {
                closeMobileMenu();
                navigate(PROTECTED_ROUTES.DASHBOARD);
              }}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              {/* Login link */}
              <Link
                to={PUBLIC_ROUTES.LOGIN}
                onClick={closeMobileMenu}
                aria-current={
                  location.pathname === PUBLIC_ROUTES.LOGIN ? 'page' : undefined
                }
                className={`py-3 text-heading-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-state-focus-inset ${
                  location.pathname === PUBLIC_ROUTES.LOGIN
                    ? 'font-bold text-primary-300'
                    : 'text-neutral-300 hover:text-neutral-50'
                }`}
              >
                Login
              </Link>

              {/* Get Started button */}
              <Button
                variant="primary"
                size="lg"
                className="mt-4 w-full"
                onClick={() => {
                  closeMobileMenu();
                  navigate(PUBLIC_ROUTES.REGISTER);
                }}
              >
                Get Started
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export default PublicNavbar;
